import type { LDMultiKindContext } from 'launchdarkly-react-client-sdk'

export interface LaunchDarklyUserDetails {
  id: string
  companyId: string
  parentCompanyId?: string | null
  email: string
}

export interface BuildLaunchDarklyContextArgs {
  userDetails: LaunchDarklyUserDetails
  /**
   * True when a superadmin/support/corporate user is acting inside another company. Callers pass a
   * `userDetails.companyId` that is ALREADY the masqueraded company (AuthorizeUser resolves it as
   * `masqueradeCompanyId || session.app_metadata.companyId`), so the two always move together.
   */
  isMasquerading?: boolean
  /** Defaults to the build-time DEPLOYMENT_STAGE; passed explicitly by tests. */
  deploymentStage?: string
}

/**
 * Maps DEPLOYMENT_STAGE onto the `environment` attribute our LaunchDarkly targeting rules match on.
 * Mirrors `getLDEnvironment` in apps/api/.../get-feature-flag-value.ts so client and server agree on
 * which environment a context belongs to.
 *
 * Guards a missing stage: the inline version this replaced called `.startsWith` on it unguarded, so
 * any bundle/test/story without DEPLOYMENT_STAGE defined threw a TypeError while rendering the
 * provider — which takes the whole authenticated tree with it.
 */
export function getLaunchDarklyEnvironment(deploymentStage?: string): string {
  if (!deploymentStage) {
    return 'dev'
  }
  if (deploymentStage === 'local' || deploymentStage.startsWith('dev')) {
    return 'dev'
  }
  if (deploymentStage === 'master') {
    return 'production'
  }
  return deploymentStage
}

/**
 * Builds the multi-kind context every client-side flag evaluation runs against.
 *
 * While masquerading, the `user` kind must NOT carry the signed-in superadmin's identity. It used to,
 * so any flag targeted at an individual user, at an email, or at a staff user segment evaluated as the
 * superadmin: masquerading reported a feature as ON that the customer could not see ("when I
 * masquerade it showed (lies though)" — ENG-988). The `companyId` kind was already correct.
 *
 * The replacement is anonymous and company-derived rather than absent:
 * - anonymous, with a key belonging to no real user, so individual targets, email clauses and staff
 *   segments stop matching — that is the actual fix;
 * - the kind stays *present* (rather than sending a companyId-only context) so user-kind percentage
 *   rollouts remain evaluable. LaunchDarkly buckets a rollout by a named kind, and a context missing
 *   that kind falls into under-specified, SDK-version-dependent behaviour;
 * - the key depends only on the company, so two staff members masquerading into the same company get
 *   the same answer as each other, and the context can be replayed in LaunchDarkly's flag debugger;
 * - companyId/parentCompanyId are kept so rules written against those user attributes keep matching
 *   the MASQUERADED company, and `anonymous` keeps these pseudo-contexts off LaunchDarkly's Contexts
 *   page (see LDContextCommon.anonymous) so we don't mint a fake user per company;
 * - `masquerading` is a marker attribute so LaunchDarkly-side rules can exclude staff sessions rather
 *   than counting them as a customer.
 *
 * Trade-off, deliberately taken: this cannot verify a flag targeted at one individual user, because
 * masquerade has no single "the customer" — a per-user target now reads OFF here even when that user
 * sees it. It converts a false positive (the bug) into a conservative false negative. The
 * authoritative per-user answer is a server-side evaluation (`getFeatureFlagDetail`), not the browser.
 */
export function buildLaunchDarklyContext({
  userDetails,
  isMasquerading,
  deploymentStage = process.env.DEPLOYMENT_STAGE
}: BuildLaunchDarklyContextArgs): LDMultiKindContext {
  const environment = getLaunchDarklyEnvironment(deploymentStage)
  const { id, companyId, parentCompanyId, email } = userDetails

  const user = isMasquerading
    ? {
        key: `masquerade-${companyId}`,
        anonymous: true,
        masquerading: true,
        companyId,
        parentCompanyId,
        environment
      }
    : {
        key: id,
        companyId,
        email,
        parentCompanyId,
        environment
      }

  return {
    kind: 'multi',
    user,
    companyId: {
      key: companyId,
      parentCompanyId,
      environment
    }
  }
}
